import React from 'react'
import { DSButton, DSText, DSSpacer } from '@zoe/ds-web'

import { appConfig } from 'appConfig'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Answer } from '../../redux/quiz/slice'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { ScreenProps } from '../types'
import { FormattedMessage } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { HEADER_BAR_HEIGHT, SCREEN_MAX_WIDTH } from '../core/constants'
import { GifName, resolveTopImage } from '../../data/gifs'

const imgFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
`

/* Tallest a full-bleed hero may be — the height the design specifies (Figma's
   welcome hero is 370px on a 375px artboard). */
const BLEED_IMAGE_MAX_HEIGHT = '370px'

/* Vertical space kept clear below a full-bleed hero for the title, the prompt
   and both rows of answer options. Measured at 330px of content across
   375-430px widths, plus ScreenContainer's 24px bottom padding and a little
   slack, so nothing overflows even by a few pixels. */
const BLEED_IMAGE_RESERVED_HEIGHT = '360px'

/* Opacity-only fade for full-bleed images, which must not scale. */
const fadeInOnly = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

// TODO: bleed variant is the LCP element on the e9 welcome screen; needs eager
// loading and fetchpriority high rather than inheriting lazy.
const TopImage = styled.img.attrs({ loading: 'lazy' })`
  display: block;
  width: 100%;
  object-fit: cover;
  border-radius: var(--radius-12);
  margin-bottom: var(--grid-24);
  animation: ${imgFadeIn} 0.3s ease-out both;
`

/* Full-bleed variant (e9 welcome): breaks out of ScreenContainer's horizontal
   padding and is pulled up behind the HeaderBar so the photo runs under the ZOE
   logo. Pair with the screen-level `overlayHeader` flag, which makes the header
   transparent and lifts it above this image.

   Deliberately avoids `transform` for the horizontal offset: TopImage's fade-in
   animation sets `transform` on its keyframes with fill-mode `both`, which wins
   over a static declaration and would leave the image uncentred. Its scale is
   dropped here too, so a bleeding image never reveals a gap at the edges. */
// TODO: offset covers the header only, so this is safe just while the screen also
// sets hideProgressBar. Fold in the progress bar height or enforce the pairing.
// TODO: near-duplicate of SocialProofScreen's BleedTopImage; extract a shared helper.
const BleedTopImage = styled(TopImage)`
  margin-top: calc(-1 * (${HEADER_BAR_HEIGHT} + var(--grid-24)));
  border-radius: 0;
  animation-name: ${fadeInOnly};

  /* The aspect ratio derives height from width, but the thing we have to fit is
     height: on a short viewport a full-width hero pushes the answer options
     below the fold. Reserve the space the copy and options need, give the image
     whatever is left, and never exceed the height the design specifies.
     svh (not vh) is the viewport with the browser toolbar showing — the honest
     worst case, and stable when the toolbar collapses on scroll. */
  max-height: min(${BLEED_IMAGE_MAX_HEIGHT}, calc(100vh - ${BLEED_IMAGE_RESERVED_HEIGHT}));
  max-height: min(${BLEED_IMAGE_MAX_HEIGHT}, calc(100svh - ${BLEED_IMAGE_RESERVED_HEIGHT}));

  /* Mirrors ScreenContainer: below its max-width the container carries 24px of
     horizontal padding to escape; above it that padding is already 0. */
  /* TODO: assumes ScreenContainer's default maxWidth; a custom one desyncs this. */
  @media (max-width: ${SCREEN_MAX_WIDTH}px) {
    width: calc(100% + 2 * var(--grid-24));
    margin-left: calc(-1 * var(--grid-24));
  }
`

/* The question slot (Ziggie asking) sits straight under the header, as in the
   Figma template: 16px below the progress bar, where ScreenContainer's top
   padding would otherwise put 32px. */
const QuestionSlot = styled.div`
  margin-top: calc(-1 * var(--grid-16));
`

export type FormValues = { [key: string]: string }
export type FormValidationSchema = any | (() => any)

export interface MultiValueFormValues {
  [key: string]: string | number
}

export interface FormProps {
  body: React.ReactNode
  initialValues?: FormValues
  validationSchema?: FormValidationSchema
}

export interface FormScreenProps extends ScreenProps {
  headerBar?: React.ReactNode
  topImage?: string
  topImageAlt?: string
  topImageAspectRatio?: string
  /** Renders topImage full-bleed, running under the header. See BleedTopImage. */
  topImageBleed?: boolean
  gif?: GifName
  aboveTitle?: React.ReactNode
  screenTitle: React.ReactNode
  // Override the screen title's DSText variant. Defaults to fluid-heading-700.
  titleVariant?: React.ComponentProps<typeof DSText>['variant']
  headlineExplanation?: React.ReactNode
  description?: React.ReactNode
  subtitleAboveTitle?: boolean
  goalAnswersToBeSubmitted?: Answer[]
  buttonText?: React.ReactNode
  forceEnableButton?: boolean // only for half-way screen where there's no form rendered
  showHomepageLink?: boolean
  form: FormProps
  valueMapper?: (values: FormValues) => FormValues
  footerSlot?: React.ReactNode
  hideButton?: boolean
  // Shown in place of the title and description, e.g. Ziggie asking the question.
  questionSlot?: React.ReactNode
}

export const FormScreen: React.FC<FormScreenProps> = ({
  headerBar,
  topImage,
  topImageAlt,
  topImageAspectRatio,
  topImageBleed,
  gif,
  aboveTitle,
  screenTitle,
  titleVariant = 'fluid-heading-700',
  headlineExplanation,
  description,
  subtitleAboveTitle,
  goalAnswersToBeSubmitted,
  buttonText,
  forceEnableButton,
  showHomepageLink,
  form,
  onNextClick,
  valueMapper,
  footerSlot,
  hideButton,
  questionSlot,
}) => {
  const { body, initialValues = {}, validationSchema } = form
  const formId = React.useId()

  const image = resolveTopImage(gif, { topImage, topImageAlt, topImageAspectRatio })
  const TopImageComponent = topImageBleed ? BleedTopImage : TopImage

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    ...(validationSchema ? { resolver: yupResolver(validationSchema) } : {}),
    mode: 'onChange',
  })

  const { handleSubmit, formState, trigger } = methods
  const { isValid, isDirty, isSubmitting } = formState

  // When the form is pre-populated with remembered values from a previous quiz
  // attempt, run validation once on mount so the Continue button correctly
  // enables without requiring the user to re-touch fields. Skip for empty
  // forms so new users don't see errors before they interact.
  const hasInitialValues = React.useMemo(
    () =>
      Object.values(initialValues).some((v) => {
        if (v === undefined || v === null || v === '') return false
        if (Array.isArray(v) && v.length === 0) return false
        return true
      }),
    [initialValues],
  )
  React.useEffect(() => {
    if (hasInitialValues) trigger()
  }, [hasInitialValues, trigger])

  const onSubmit = (values: FormValues) => {
    values = valueMapper ? valueMapper(values) : values
    onNextClick(values, goalAnswersToBeSubmitted)
  }

  return (
    <div>
      {headerBar}
      <ScreenContainer>
        {image.topImage && (
          <TopImageComponent
            src={image.topImage}
            alt={image.topImageAlt ?? ''}
            style={image.topImageAspectRatio ? { aspectRatio: image.topImageAspectRatio } : undefined}
          />
        )}
        {questionSlot ? (
          <QuestionSlot>{questionSlot}</QuestionSlot>
        ) : (
          <>
            {subtitleAboveTitle && description && (
              <>
                <DSText variant="fluid-paragraph-400" color="secondary">
                  {description}
                </DSText>
                <DSSpacer size={8} direction="vertical" />
              </>
            )}
            {aboveTitle && (
              <>
                {aboveTitle}
                <DSSpacer size={16} direction="vertical" />
              </>
            )}
            <DSText variant={titleVariant} weight="semiBold" align="left">
              {screenTitle}
            </DSText>
            {headlineExplanation && (
              <>
                <DSSpacer size={32} direction="vertical" />
                <DSText variant="fluid-heading-500" weight="semiBold">
                  {headlineExplanation}
                </DSText>
                <DSSpacer size={24} direction="vertical" />
              </>
            )}

            {!subtitleAboveTitle && description && (
              <>
                <DSSpacer size={16} direction="vertical" />
                <DSText variant="fluid-paragraph-400" color="secondary">
                  {description}
                </DSText>
              </>
            )}
          </>
        )}

        {form && (
          <FormProvider {...methods}>
            <form id={formId} onSubmit={handleSubmit(onSubmit)}>
              <DSSpacer size={24} direction="vertical" />
              {body}
              {!hideButton && (
                <>
                  <StickyFooter $expanded={!!footerSlot}>
                    <StickyFooterContent>
                      {footerSlot}
                      <DSButton
                        variant="primary"
                        colorScheme="default"
                        size="large"
                        width="full"
                        type="submit"
                        /* StickyFooter portals its DOM out of the <form>, so the
                           button is no longer a descendant and needs an explicit
                           association to submit it. */
                        form={formId}
                        isDisabled={!(forceEnableButton || isValid) || isSubmitting}
                        testId="continue-button"
                      >
                        <DSText variant="fluid-paragraph-200" weight="semiBold">
                          {buttonText || <FormattedMessage id="next" />}
                        </DSText>
                      </DSButton>
                      {showHomepageLink && !(isValid && isDirty) && (
                        <a
                          href={`${appConfig.zoeWebsiteBaseUri}`}
                          data-testid="homepage-link"
                          style={{ marginTop: 'var(--grid-16)', display: 'block', color: 'var(--colour-text-link)' }}
                        >
                          Return to homepage
                        </a>
                      )}
                    </StickyFooterContent>
                  </StickyFooter>
                </>
              )}
            </form>
          </FormProvider>
        )}
      </ScreenContainer>
    </div>
  )
}
